import ResizeObserver from 'resize-observer-polyfill';

/**
 * @constant {Object}
 */
const CAROUSEL_CLASSES = {
  HIDDEN_BUTTON_CLASS: 'mc-carousel-header__buttons--hidden',
  HIDDEN_SCROLLBAR_CLASS: 'mc-carousel__custom-scrollbar--hidden',
  DISABLED_BUTTON_CLASS: 'mc-carousel-header__button--disabled',
  CENTERED_ITEMS_CLASS: 'mc-carousel-body__item-list--center',
  NO_DRAG: 'no-drag',
  SCROLLABLE_CLASS: 'mc-carousel-body--scrollable',
  CAROUSEL_CONTAINER: 'js-carousel-container',
  CAROUSEL_CONTENT: 'js-carousel-content',
  CAROUSEL_ITEMS: 'js-carousel-item-list',
  CAROUSEL_CONTROLS: 'js-carousel-controls',
  CAROUSEL_BUTTON: 'js-carousel-button',
  CUSTOM_SCROLLBAR: 'js-scrollbar',
  CUSTOM_SCROLLBAR_THUMB: 'js-scrollbar-thumb'
};
class Carousel {
  /**
   * @param {Element|string} elementOrSelector The DOM element or selector string for the carousel.
   * @param {Object} [options={}] options Additional configuration options.
   */
  constructor(elementOrSelector, options = {}) {
    this.carousel = this.getCarousel(elementOrSelector);
    this.carouselItems = this.carousel.querySelectorAll(`.${CAROUSEL_CLASSES.CAROUSEL_ITEMS} > li`);
    this.buttonsNavigation = this.carousel.querySelectorAll(`.${CAROUSEL_CLASSES.CAROUSEL_BUTTON}`);
    this.options = {
      centerWhenNoButtons: false,
      ...options
    };
    this.scrollWidth = this.carouselItems[0] ? this.getFullWidth(this.carouselItems[0]) : 0;
    this.isDragging = false;
    this.initializeScrollbar();
    this.initializeEventListeners();
    this.buttonsVisibility();
  }
  /**
   * @param {HTMLElement|string} elementSelector The DOM element or selector string for the carousel
   * @returns {HTMLElement}
   */
  getCarousel(elementSelector) {
    return typeof elementSelector === 'string' ? document.querySelector(elementSelector) : elementSelector;
  }
  /**
   * @param {HTMLElement} element The DOM element
   * @returns {number}
   */
  getFullWidth(element) {
    const elementStyle = window.getComputedStyle(element);
    return element.clientWidth + parseFloat(elementStyle.marginLeft) + parseFloat(elementStyle.marginRight) + parseFloat(elementStyle.paddingLeft) + parseFloat(elementStyle.paddingRight) + parseFloat(elementStyle.borderLeftWidth) + parseFloat(elementStyle.borderRightWidth);
  }
  initializeEventListeners() {
    this.resizeObserver = new ResizeObserver(() => this.refreshLayout());
    this.resizeObserver.observe(this.carousel);
    this.resizeObserver.observe(this.content);
    this.refreshLayoutHandler = () => this.refreshLayout();
    window.addEventListener('resize', this.refreshLayoutHandler);
    window.addEventListener('guidance:dom:updated', this.refreshLayoutHandler);
    if (!this.eventLoaded) {
      this.buttonsNavigation.forEach(button => {
        button.addEventListener('click', event => this.swipe(event.currentTarget));
      });
      this.eventLoaded = true;
    }
  }
  refreshLayout() {
    if (!this.carousel?.isConnected || !this.content?.isConnected) {
      return;
    }
    if (this.content.clientWidth === 0) {
      this.disableNavigation();
      return;
    }
    this.scrollWidth = this.carouselItems[0] ? this.getFullWidth(this.carouselItems[0]) : 0;
    this.buttonsVisibility();
    this.updateThumbWidth();
    this.updateThumbPosition();
  }
  destroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.refreshLayoutHandler) {
      window.removeEventListener('resize', this.refreshLayoutHandler);
      window.removeEventListener('guidance:dom:updated', this.refreshLayoutHandler);
    }
  }
  disableNavigation() {
    this.buttonsNavigation.forEach(button => {
      button.classList.add(CAROUSEL_CLASSES.DISABLED_BUTTON_CLASS);
      button.toggleAttribute('disabled', true);
    });
  }
  buttonsVisibility() {
    if (!this.carousel?.isConnected || !this.content?.isConnected || this.content.clientWidth === 0) {
      this.disableNavigation();
      return;
    }
    const carouselWidth = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_CONTENT}`).offsetWidth;
    const carouselSlider = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_ITEMS}`);
    const carouselSliderWidth = carouselSlider.scrollWidth;
    const buttonsNavigation = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_CONTROLS}`);
    const scrollbar = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CUSTOM_SCROLLBAR}`);
    if (carouselSliderWidth > carouselWidth) {
      buttonsNavigation.classList.remove(`${CAROUSEL_CLASSES.HIDDEN_BUTTON_CLASS}`);
      carouselSlider.classList.remove(`${CAROUSEL_CLASSES.CENTERED_ITEMS_CLASS}`);
      scrollbar.classList.remove(`${CAROUSEL_CLASSES.HIDDEN_SCROLLBAR_CLASS}`);
      carouselSlider.classList.add(`${CAROUSEL_CLASSES.SCROLLABLE_CLASS}`);
    } else {
      buttonsNavigation.classList.add(`${CAROUSEL_CLASSES.HIDDEN_BUTTON_CLASS}`);
      scrollbar.classList.add(`${CAROUSEL_CLASSES.HIDDEN_SCROLLBAR_CLASS}`);
      carouselSlider.classList.remove(`${CAROUSEL_CLASSES.SCROLLABLE_CLASS}`);
      if (this.options.centerWhenNoButtons) {
        carouselSlider.classList.add(`${CAROUSEL_CLASSES.CENTERED_ITEMS_CLASS}`);
      }
    }
  }
  /**
   * @param {HTMLElement} carouselScrollElement The DOM element of the carousel scroll container
   * @param {number} scrollValue The scroll value to be applied
   */
  checkStatusButtons(carouselScrollElement, scrollValue) {
    if (!carouselScrollElement?.isConnected || carouselScrollElement.clientWidth === 0) {
      this.disableNavigation();
      return;
    }
    const limit = Math.ceil(carouselScrollElement.scrollWidth - carouselScrollElement.clientWidth);
    const scrollPosition = Math.round(carouselScrollElement.scrollLeft + scrollValue);
    this.buttonsNavigation[0].classList.toggle(CAROUSEL_CLASSES.DISABLED_BUTTON_CLASS, scrollPosition <= 0);
    this.buttonsNavigation[1].classList.toggle(CAROUSEL_CLASSES.DISABLED_BUTTON_CLASS, scrollPosition >= limit);
    this.buttonsNavigation[0].toggleAttribute('disabled', scrollPosition <= 0);
    this.buttonsNavigation[1].toggleAttribute('disabled', scrollPosition >= limit);
  }
  /**
   * @param {HTMLElement} targetButton The DOM element for the navigation button
   */
  swipe = function (targetButton) {
    const carousel = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_CONTENT}`);
    let partialItemWidth = 0;
    const isPrev = targetButton.dataset.controls === 'prev';
    for (const item of this.carouselItems) {
      const itemLeft = item.getBoundingClientRect().left;
      const carouselLeft = carousel.getBoundingClientRect().left;
      const itemRight = item.getBoundingClientRect().right;
      const carouselRight = carousel.getBoundingClientRect().right;
      const itemSize = itemRight - itemLeft;
      const carrouselSize = carouselRight - carouselLeft;
      const itemIsBiggerThanCarrousel = itemSize > carrouselSize;
      const clickLeft = isPrev && itemLeft < carouselLeft && itemRight > carouselLeft;
      const clickRigth = !isPrev && itemLeft < carouselRight && itemRight > carouselRight;
      if (itemIsBiggerThanCarrousel) {
        const style = window.getComputedStyle(item);
        const spacing = isPrev ? parseFloat(style.paddingLeft) + parseFloat(style.marginLeft) : parseFloat(style.paddingRight) + parseFloat(style.marginRight);
        partialItemWidth = carrouselSize - itemSize - spacing;
        break;
      }
      if (clickLeft || clickRigth) {
        partialItemWidth = clickLeft ? itemRight - carouselLeft : carouselRight - itemLeft;
        break;
      }
    }
    const visibleWidth = carousel.offsetWidth;
    const scrollValue = isPrev ? (visibleWidth - partialItemWidth) * -1 : visibleWidth - partialItemWidth;
    this.showButton(carousel, scrollValue);
    carousel.scrollBy({
      top: 0,
      left: scrollValue,
      behavior: 'smooth'
    });
  };
  /**
   * @param {HTMLElement} scrollElement The DOM element of the carousel scroll container
   * @param {number} scrollValue The scroll value to be applied
   */
  showButton(scrollElement, scrollValue) {
    this.checkStatusButtons(scrollElement, scrollValue);
  }
  initializeScrollbar() {
    this.container = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_CONTAINER}`);
    this.content = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CAROUSEL_CONTENT}`);
    this.scrollbar = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CUSTOM_SCROLLBAR}`);
    this.thumb = this.carousel.querySelector(`.${CAROUSEL_CLASSES.CUSTOM_SCROLLBAR_THUMB}`);
    this.updateThumbPosition();
    this.updateThumbWidth();
    this.thumb.addEventListener('mousedown', this.onThumbMouseDown.bind(this));
    this.scrollbar.addEventListener('click', this.onScrollbarClick.bind(this));
    this.content.addEventListener('scroll', this.updateThumbPosition.bind(this));
  }
  /**
   * @param {Event} event The mouse down event
   */
  onThumbMouseDown(event) {
    const startX = event.pageX;
    const startScrollLeft = this.content.scrollLeft;
    this.isDragging = true;
    document.body.classList.add(CAROUSEL_CLASSES.NO_DRAG);
    document.addEventListener('mousemove', this.onMouseMove.bind(this, startX, startScrollLeft));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
  }
  updateThumbPosition() {
    if (!this.content?.isConnected || this.content.clientWidth === 0 || this.container.offsetWidth === 0) {
      this.disableNavigation();
      return;
    }
    const scrollableWidth = this.content.scrollWidth - this.container.offsetWidth;
    const scrollRatio = scrollableWidth > 0 ? this.content.scrollLeft / scrollableWidth : 0;
    this.thumb.style.left = `${scrollRatio * (this.scrollbar.offsetWidth - this.thumb.offsetWidth)}px`;
    this.checkStatusButtons(this.content, 0);
    this.carousel.classList.toggle('mc-carousel--scrolled', this.content.scrollLeft > 0);
  }
  updateThumbWidth() {
    if (!this.content?.isConnected || this.container.offsetWidth === 0 || this.content.scrollWidth === 0) {
      return;
    }
    const ratio = this.container.offsetWidth / this.content.scrollWidth;
    this.thumb.style.width = `${100 * ratio}%`;
  }
  /**
   * @param {number} startX The starting X coordinate of the mouse event
   * @param {number} startScrollLeft The initial scroll position
   * @param {Event} event The mouse move event
   */
  onMouseMove(startX, startScrollLeft, event) {
    if (!this.isDragging) return;
    event.preventDefault();
    const dx = event.pageX - startX;
    const maxThumbMove = this.scrollbar.offsetWidth - this.thumb.offsetWidth;
    const scrollRatio = dx / maxThumbMove;
    const scrollDifference = scrollRatio * (this.content.scrollWidth - this.container.offsetWidth) - this.content.scrollLeft + startScrollLeft;
    this.content.scrollBy({
      top: 0,
      left: scrollDifference,
      behavior: 'instant'
    });
    this.updateThumbPosition();
  }
  onMouseUp() {
    this.isDragging = false;
    document.body.classList.remove(CAROUSEL_CLASSES.NO_DRAG);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  }
  /**
   * @param {Event} event The click event on the scrollbar
   */
  onScrollbarClick(event) {
    if (event.target === this.thumb) return;
    const clickX = event.pageX - this.scrollbar.getBoundingClientRect().left;
    const thumbHalfWidth = this.thumb.offsetWidth / 2;
    const clickRatio = (clickX - thumbHalfWidth) / (this.scrollbar.offsetWidth - this.thumb.offsetWidth);
    this.content.scrollLeft = clickRatio * (this.content.scrollWidth - this.container.offsetWidth);
    this.updateThumbPosition();
  }
}
export default Carousel;